import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Sparkles } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, CardContent, DataTable, PageHeader, Select, useToast } from '@/components/ui';
import { api } from '@/lib/api';
const STAGES = ['applied', 'screen', 'assessment', 'interview', 'offer', 'hired', 'rejected', 'withdrawn'];
export function ApplicationsPage() {
    const qc = useQueryClient();
    const [params, setParams] = useSearchParams();
    const job = params.get('job') ?? '';
    const stage = params.get('stage') ?? '';
    const url = (() => {
        const p = new URLSearchParams();
        if (job)
            p.set('job', job);
        if (stage)
            p.set('stage', stage);
        return `/hrms/applications/?${p.toString()}`;
    })();
    const list = useQuery({ queryKey: ['applications', job, stage], queryFn: async () => (await api.get(url)).data });
    const updateStage = useMutation({
        mutationFn: async ({ id, stage }) => (await api.patch(`/hrms/applications/${id}/`, { stage })).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['applications'] }),
    });
    const toast = useToast();
    const rescore = useMutation({
        mutationFn: async (id) => (await api.post(`/hrms/applications/${id}/score/`)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['applications'] });
            toast.success('Match score refreshed');
        },
        onError: (err) => toast.error('Score failed', err.response?.data?.detail || err.message),
    });
    return (_jsxs("div", { className: "space-y-4", children: [_jsx(PageHeader, { title: "Applications", description: "Candidate \u00D7 Job. AI match score is computed automatically when the resume is uploaded." }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("span", { className: "text-xs text-muted-foreground", children: "Stage:" }), _jsxs(Select, { value: stage, onChange: (e) => {
                            if (e.target.value)
                                params.set('stage', e.target.value);
                            else
                                params.delete('stage');
                            setParams(params, { replace: true });
                        }, children: [_jsx("option", { value: "", children: "All" }), STAGES.map((s) => _jsx("option", { value: s, children: s }, s))] }), (job || stage) && (_jsx("button", { className: "text-xs text-primary hover:underline", onClick: () => { setParams(new URLSearchParams(), { replace: true }); }, children: "Clear" }))] }), _jsx(DataTable, { rows: list.data, rowKey: (r) => r.id, loading: list.isLoading, columns: [
                    { key: 'cand', header: 'Candidate', render: (a) => a.candidate_name },
                    { key: 'job', header: 'Job', render: (a) => a.job_title },
                    { key: 'stage', header: 'Stage',
                        render: (a) => (_jsx(Select, { value: a.stage, onChange: (e) => updateStage.mutate({ id: a.id, stage: e.target.value }), children: STAGES.map((s) => _jsx("option", { value: s, children: s }, s)) })) },
                    { key: 'match', header: 'AI match', align: 'right',
                        render: (a) => {
                            const score = Number(a.match_score);
                            return (_jsxs("span", { className: `inline-flex items-center gap-1 font-mono ${score > 70 ? 'text-emerald-600' : score > 40 ? 'text-amber-600' : 'text-muted-foreground'}`, children: [_jsx(Sparkles, { className: "h-3 w-3" }), " ", score.toFixed(1)] }));
                        } },
                    { key: 'why', header: 'AI explanation',
                        render: (a) => _jsx("span", { className: "text-xs text-muted-foreground line-clamp-2", children: a.match_explanation || '—' }) },
                    { key: 're', header: '', align: 'right',
                        render: (a) => (_jsx(Button, { size: "sm", variant: "ghost", onClick: () => rescore.mutate(a.id), title: "Recompute the AI match score for this application.", children: _jsx(RefreshCw, { className: "h-3 w-3" }) })) },
                ] })] }));
}
